import { formatPrice } from '@dashboard/services/formatting';

// The store names the fee with the theme label `bookingFeeText`; the editor
// summary uses the default wording.
const BOOKING_FEE_NAME = 'booking fee';

const getFeeAmount = ticketType => Number(ticketType?.feeAmount) || 0;

/**
 * A ticket type's amount headline: "$10" / "Free", or for pay-what-you-want
 * "Suggested $10" / "Min. $10" / "No min. price". Mirrors the price rows'
 * getPriceHeadline so tickets and prices read the same, and — like the checkout
 * placeholder — prefers the suggested amount over the minimum. A pay-what-you-want
 * ticket is never "Free": its minimum can be 0 while the buyer still pays.
 *
 * A ticket fee is folded into the headline ("$13") when the ticket group shows
 * fees included in the price; otherwise the headline is the price alone and
 * getTicketFeeNote carries the fee underneath. A free ticket stays "Free": its
 * fee is the whole charge, so folding it in would just show the fee twice.
 */
export default function getTicketPriceHeadline({ ticketType, currency, locale, feeDisplay }) {
  if (!ticketType) return '';

  const money = amount => formatPrice({ amount, currency, locale });

  if (ticketType.pricing === 'pwyw') {
    const min = Number(ticketType.price) || 0;
    const suggested = Number(ticketType.pwywSuggestedPrice) || 0;

    // A suggested price below the minimum can only come from unvalidated API
    // input — never advertise an amount the checkout would reject.
    if (suggested > 0 && suggested >= min) return `Suggested ${money(suggested)}`;
    return min > 0 ? `Min. ${money(min)}` : 'No min. price';
  }

  const price = Number(ticketType.price) || 0;
  const feeAmount = getFeeAmount(ticketType);

  if (price > 0 && feeAmount > 0 && feeDisplay !== 'separate') {
    return money(price + feeAmount);
  }

  return price > 0 ? money(price) : 'Free';
}

/**
 * The fee note under the headline: "Includes $3 booking fee" when the group
 * folds fees into the price, "$3 booking fee" when it shows them separately
 * (and always for pay-what-you-want and free tickets, where there is no price
 * to fold it into). Null when the ticket has no fee.
 */
export function getTicketFeeNote({ ticketType, currency, locale, feeDisplay }) {
  const feeAmount = getFeeAmount(ticketType);
  if (!feeAmount) return null;

  const fee = `${formatPrice({ amount: feeAmount, currency, locale })} ${BOOKING_FEE_NAME}`;
  const includedInPrice =
    ticketType.pricing !== 'pwyw' && Number(ticketType.price) > 0 && feeDisplay !== 'separate';

  return includedInPrice ? `Includes ${fee}` : fee;
}
